import { messages as pasteMessages } from '@databases/copy-paste/messages';
import { pasteContentExperience } from '@databases/frontend-metrics';
import { getService } from '@databases/service-container';
import { showErrorSnackbar } from '@databases/utils/errors';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

interface UseAttachmentPasteOptions {
  entryId: string | undefined;
  fieldId: string;
  /**
   * Callback to handle dropped files
   */
  handleDroppedFiles: (files: File[]) => void;
}

export const useAttachmentPaste = ({
  entryId,
  fieldId,
  handleDroppedFiles,
}: UseAttachmentPasteOptions) => {
  const { formatMessage } = useIntl();

  const handlePaste = useCallback(
    (items: DataTransferItemList) => {
      if (!entryId) {
        return;
      }

      const reader = getService('SelectionsReader');
      if (reader.getSelectionCount() !== 1 || !reader.isValueSelected(entryId, fieldId)) {
        return;
      }

      if (items.length === 0) {
        return;
      }

      const imageItems = Array.from(items).filter((item) =>
        item.type.startsWith('image/')
      );

      if (imageItems.length === 0) {
        return;
      }

      try {
        pasteContentExperience.start();

        const firstImageFile = imageItems[0]?.getAsFile();
        if (firstImageFile) {
          handleDroppedFiles([firstImageFile]);
          pasteContentExperience.success();
          return;
        }
      } catch (e) {
        showErrorSnackbar(
          'cannot-read-pasted-content',
          formatMessage(pasteMessages['flag-error-message.cannot-read-pasted-content'])
        );
        pasteContentExperience.failure();
        return;
      }
    },
    [entryId, fieldId, formatMessage, handleDroppedFiles]
  );

  return { handlePaste };
};
